import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Avisa na hora quando uma ativação vira Ativado ou Incorreto.
 * Canto inferior direito no desktop; acima da ilha no celular.
 */
const INTERVALO = 10000;   // consulta a cada 10s
const DURACAO = 7000;      // cada aviso fica 7s na tela
const MAX_VISIVEIS = 3;
const DESCANSO = 120000;   // a mesma ativação não repete antes de 2 min

const ActivationAlerts = () => {
  const { api, user, refreshUser } = useAuth();
  const [avisos, setAvisos] = useState([]);
  const desde = useRef(new Date().toISOString());
  const jaMostrados = useRef(new Set());
  const ultimoPorAtivacao = useRef(new Map());

  const remover = useCallback((id) => {
    setAvisos((atuais) => atuais.filter((a) => a.id !== id));
  }, []);

  const buscar = useCallback(async () => {
    if (document.hidden) return;   // aba em segundo plano não consulta
    try {
      const { data } = await api.get('/api/activations/updates', { params: { since: desde.current } });
      if (data?.now) desde.current = data.now;

      const agora = Date.now();
      const novos = (data?.items || []).filter((item) => {
        // Só o que mudou nos últimos 3 minutos, para não ressuscitar coisa velha
        const quando = Date.parse(item.updated_at || '');
        if (quando && agora - quando > 180000) return false;

        // O horário entra na chave: reenviar e dar erro de novo avisa de novo
        const chave = `${item.id}:${item.status}:${item.updated_at}`;
        if (jaMostrados.current.has(chave)) return false;

        // Mesmo que o registro seja tocado de novo, a mesma ativação não
        // enche o canto da tela repetidamente
        const visto = ultimoPorAtivacao.current.get(item.id);
        if (visto && agora - visto < DESCANSO) return false;

        jaMostrados.current.add(chave);
        ultimoPorAtivacao.current.set(item.id, agora);
        if (jaMostrados.current.size > 200) jaMostrados.current = new Set();
        return true;
      });

      if (novos.length > 0) {
        setAvisos((atuais) => [...novos, ...atuais].slice(0, MAX_VISIVEIS));   // mais novo no topo
        novos.forEach((item) => setTimeout(() => remover(item.id), DURACAO));

        // O cashback só é creditado quando a ativação confirma, depois que a
        // tela já carregou. Sem isto o saldo no topo só mudava ao recarregar.
        if (novos.some((item) => item.status === 'Ativado')) {
          refreshUser?.();
        }
      }
    } catch (error) {
      // silencioso: é um aviso, não pode atrapalhar a navegação
    }
  }, [api, remover, refreshUser]);

  useEffect(() => {
    if (!user) return undefined;
    const timer = setInterval(buscar, INTERVALO);
    return () => clearInterval(timer);
  }, [user, buscar]);

  if (!user) return null;

  return (
    <div className="fixed right-3 z-[70] flex flex-col gap-2 pointer-events-none
                    bottom-[104px] md:bottom-5 md:right-5 w-[calc(100%-24px)] max-w-[330px]">
      <AnimatePresence initial={false}>
        {avisos.map((item) => {
          const ok = item.status === 'Ativado';
          return (
            <motion.div
              key={`${item.id}-${item.status}-${item.updated_at}`}
              layout
              initial={{ opacity: 0, x: 40, scale: 0.96 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 40, scale: 0.96 }}
              transition={{ type: 'spring', damping: 26, stiffness: 320 }}
              className="pointer-events-auto flex gap-2.5 rounded-[13px] border-2 bg-[#13101C] px-3.5 py-3 shadow-[0_14px_40px_rgba(0,0,0,0.6)]"
              style={{ borderColor: ok ? 'rgba(69,212,168,0.4)' : 'rgba(248,113,113,0.4)' }}
            >
              <span
                className="w-7 h-7 rounded-[9px] flex items-center justify-center flex-shrink-0"
                style={{
                  background: ok ? 'rgba(69,212,168,0.16)' : 'rgba(248,113,113,0.16)',
                  color: ok ? '#45D4A8' : '#F87171',
                }}
              >
                {ok ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />}
              </span>

              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-white leading-tight">
                  {ok ? 'Ativação concluída' : 'Ativação incorreta'}
                </p>
                <p className="text-[12px] text-[#CFC8DD] truncate">{item.app_name}</p>
                {item.mac_address && (
                  <p className="text-[11px] text-[#938BA8] font-mono truncate">{item.mac_address}</p>
                )}
                {!ok && item.error_reason && (
                  <p className="text-[11px] text-[#F87171] mt-0.5 line-clamp-2">{item.error_reason}</p>
                )}
              </div>

              <button
                onClick={() => remover(item.id)}
                aria-label="Fechar aviso"
                className="text-[#6E667F] hover:text-white flex-shrink-0"
              >
                <X size={15} />
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

export default ActivationAlerts;
